import React from "react"
import { styled } from "@mui/material/styles"
import { css } from '@emotion/react';
import axios from "axios"
import {
  Badge,
  Link,
  ListItemText,
} from "@mui/material"
import { getCsrfToken } from "../utils/userUtils"
import NotificationsFeed from "./NotificationsFeed"
import ThemeProviderWrapper from "./ThemeProviderWrapper";

const NotificationsContainer = styled('div')`
  position: relative;
  margin: 0 3px;
`

const NotificationIcon = styled('div')`
  font-size: 19px;
  margin: 0 5px 0px 5px;
  cursor: pointer;
`

const NotificationsDropdownMenu = styled('div')(({ isExpanded }) =>`
  position: absolute;
  display: flex;
  flex-direction: column;
  max-height: 30vmax;
  background-color: white;
  color: black;
  width: 300px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  right: 0;
  border-radius: 5px;
  visibility: ${isExpanded ? 'visible' : 'hidden'};
  opacity: ${isExpanded ? 1 : 0};
  transform: ${isExpanded ? 'translateY(0)' : 'translateY(-20px)'};
  transition:
    visibility 0s,
    opacity 0.5s ease,
    transform 0.5s ease;
`)

const NotificationsHeaderContainer = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  border-bottom: 0.3px solid #999999;
`

const NotificationsHeader = styled('h6')`
  font-weight: bold;
  margin: 8px 0px 8px 15px;
  font-size: 17px;
`

const SettingsLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`

const NotificationSettings = styled('p')`
  color: #999999;
  font-size: 13px;
  font-weight: 400;
  margin: 0px 5px;
  line-height: 3;
`

const NotificationsListWrapper = styled('div')`
  overflow-y: auto;
`

const NotificationsLink = styled(Link)`
  text-align: center;
  text-decoration: none;
  &:hover {
    color: #007bff;
    text-decoration: none;
  }
`

const ViewAllListItemText = styled(ListItemText)`
  margin: 0px;
  padding: 0px;
  position: sticky;
`

export default class NotificationsDropdown extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      unreadCount: this.props.unreadCount,
      isExpanded: false,
    }
    this.dropdownRef = React.createRef()
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isExpanded && !prevState.isExpanded) {
      document.addEventListener("click", this.handleOutsideClick, false)
    }

    if (!this.state.isExpanded && prevState.isExpanded) {
      document.removeEventListener("click", this.handleOutsideClick, false)
    }
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleOutsideClick, false)
  }

  handleNotificationIconClick = (event) => {
    event.stopPropagation()
    this.setState((prevState) => ({ isExpanded: !prevState.isExpanded }))
    if (!this.state.isExpanded) {
      this.markUnreadNotificationsAsRead()
    }
  }

  handleOutsideClick = (event) => {
    if (
      this.dropdownRef.current &&
      !this.dropdownRef.current.contains(event.target)
    ) {
      this.setState({ isExpanded: false })
    }
  }

  handleNotificationIconKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      this.setState((prevState) => ({ isExpanded: !prevState.isExpanded }))
      if (!this.state.isExpanded) {
        this.markUnreadNotificationsAsRead()
      }
    }
  }

  markUnreadNotificationsAsRead = async () => {
    axios.defaults.headers.common["X-CSRF-Token"] = getCsrfToken()

    try {
      await axios.patch(`/notifications/mark_read`)
      this.setState({ unreadCount: 0 })
    } catch (error) {
      console.error("Error marking notification as read.", error)
    }
  }

  renderNotificationIcon = () => {
    const { unreadCount } = this.state

    return (
      <Badge badgeContent={unreadCount} color="primary" sx={{ top: -2 }}>
        <NotificationIcon
          className="fas fa-bell"
          onClick={this.handleNotificationIconClick}
          tabIndex="0"
          onKeyDown={this.handleNotificationIconKeyDown}
          role="button" 
          aria-label={`Notifications dropdown - ${unreadCount} unread notifications`}
        />
      </Badge>
    )
  }

  render() {
    const { currentUser } = this.props
    const { isExpanded } = this.state

    return (
      <ThemeProviderWrapper>
        <NotificationsContainer>
          {this.renderNotificationIcon()}
          <NotificationsDropdownMenu
            isExpanded={isExpanded}
            ref={this.dropdownRef}
          >
            <span
              css={css`
              display: flex;
              justify-content: center;
            `}
            >
              <NotificationsHeaderContainer>
                <NotificationsHeader>Notifications</NotificationsHeader>
                <SettingsLink
                  href={`/users/${currentUser.slug}/notification_settings`}
                >
                  <NotificationSettings aria-label="Notification settings">Settings</NotificationSettings>
                </SettingsLink>
              </NotificationsHeaderContainer>
            </span>
            <NotificationsListWrapper>
              <NotificationsFeed isActive={isExpanded} showLoadMore={false} currentUser={currentUser} />
            </NotificationsListWrapper>
            <NotificationsLink color="secondary" href="/notifications">
              <ViewAllListItemText primary="View all notifications" />
            </NotificationsLink>
          </NotificationsDropdownMenu>
        </NotificationsContainer>
      </ThemeProviderWrapper>
    )
  }
}
